import { Component } from '@theme/component';

/**
 * Hero trust-badge marquee — same clone + -50% loop as announcement-bar.js.
 *
 * @extends {Component<{}>}
 */
export class HeroBadgesMarquee extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#setupMarquee();
    this.#resizeObserver = new ResizeObserver(() => this.#setupMarquee());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #setupMarquee() {
    const track = this.querySelector('[data-hero-badges-track]');
    const items = this.querySelector('[data-hero-badges-items]');
    if (!(track instanceof HTMLElement) || !(items instanceof HTMLElement)) return;

    track.querySelectorAll('[data-hero-badges-clone]').forEach((node) => node.remove());
    items.querySelectorAll('[data-hero-badge-fill]').forEach((node) => node.remove());
    track.removeAttribute('data-marquee-ready');

    const sourceItems = [...items.querySelectorAll('[data-hero-badge-item]')];
    if (sourceItems.length === 0) return;

    const viewportWidth = this.querySelector('[data-hero-badges-marquee]')?.clientWidth || this.clientWidth;

    while (items.scrollWidth < viewportWidth && items.children.length < 40) {
      for (const item of sourceItems) {
        const fill = /** @type {HTMLElement} */ (item.cloneNode(true));
        fill.setAttribute('data-hero-badge-fill', '');
        fill.setAttribute('aria-hidden', 'true');
        items.appendChild(fill);
        if (items.scrollWidth >= viewportWidth) break;
      }
    }

    const clone = /** @type {HTMLElement} */ (items.cloneNode(true));
    clone.setAttribute('data-hero-badges-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('role');
    track.appendChild(clone);

    const speedSetting = Number(this.dataset.speed || 5);
    const pixelsPerSecond = Math.max(28, 110 / Math.max(speedSetting, 1));
    const duration = Math.max(12, items.scrollWidth / pixelsPerSecond);

    this.style.setProperty('--hero-badges-marquee-duration', `${duration.toFixed(2)}s`);
    track.setAttribute('data-marquee-ready', 'true');
  }
}

if (!customElements.get('hero-badges-marquee-component')) {
  customElements.define('hero-badges-marquee-component', HeroBadgesMarquee);
}
